import axios from 'axios';

// A "session expired" prompt only makes sense if a session ever existed.
// Logged-out visitors on public pages (e.g. /unauthorized) still trigger 401s
// from layout chrome (Sidebar portlet fetch, etc.) and must not see the modal.
const hasStoredSession = () => {
  const stored = localStorage.getItem('currentUser');
  return !!stored && stored !== 'undefined';
};

// Register the SAME 401/403 suppression on the default `axios` instance. ~30
// iteach files (student-affairs pages, etc.) call raw `axios` instead of
// myAxios; without this their 401s reject unhandled and hit CRA's "Uncaught
// runtime errors" overlay. Guarded so it only registers once even if this
// module is evaluated from multiple import sites.
if (!axios.__cwAuthSuppressInstalled) {
  axios.__cwAuthSuppressInstalled = true;
  axios.interceptors.response.use(
    response => response,
    error => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';
      if ((status === 401 || status === 403) && error?.config?.responseType !== 'blob') {
        const isLoginUrl = url.includes('/authenticate/user');
        if (status === 401 && !isLoginUrl && hasStoredSession()) {
          window.dispatchEvent(new CustomEvent('iteach:session-expired'));
        }
        console.warn('[axios] Auth error suppressed (plain axios):', status, url);
        return Promise.resolve({
          data: {},
          status: 200,
          statusText: 'OK (suppressed auth error)',
          headers: {},
          config: error.config,
          // Original 401/403 status, so callers can distinguish a permissions
          // failure from a genuinely empty response.
          suppressedAuthStatus: status
        });
      }
      return Promise.reject(error);
    }
  );
}

  const instance = axios.create();

  instance.interceptors.request.use(
    config => {
      const currentUser = localStorage.getItem('currentUser');
      if (currentUser && currentUser !== 'undefined') {
        try {
          const user = JSON.parse(currentUser);
          const token = user.token;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Failed to parse currentUser from localStorage:', error);
        }
      }
      return config;
    },
    error => Promise.reject(error)
  );

  instance.interceptors.response.use(
    response => response,
    error => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';

      // Suppress 401/403 — these are session/auth issues, not app bugs, and an
      // unhandled rejection otherwise lands in CRA's "Uncaught runtime errors"
      // overlay. Return a benign empty body so callers' .then handlers run with
      // empty data (components handle missing data via their own state).
      // Exception: binary downloads (responseType: 'blob') must reject — a fake
      // {} body would become a garbage blob and hide the failure.
      if ((status === 401 || status === 403) && error?.config?.responseType !== 'blob') {
        // Wrong-password 401s on the login endpoint must surface to the login
        // form, not the session-expired modal. 403 (insufficient permission)
        // does not fire the modal — the user is still authenticated.
        const isLoginUrl = url.includes('/authenticate/user');
        if (status === 401 && !isLoginUrl && hasStoredSession()) {
          window.dispatchEvent(new CustomEvent('iteach:session-expired'));
        }
        console.warn('[axios] Auth error suppressed:', status, url);
        return Promise.resolve({
          data: {},
          status: 200,
          statusText: 'OK (suppressed auth error)',
          headers: {},
          config: error.config,
          // Original 401/403 status, so callers can distinguish a permissions
          // failure from a genuinely empty response.
          suppressedAuthStatus: status
        });
      }

      return Promise.reject(error);
    }
  );

  export default instance;